import config from 'player/config';

import { TOKEN_FETCH_ERROR } from '../playback/config/status-codes';
import type { StreamModel } from './stream';

class TokenV2 {
    data: TokenPayload | Record<string, never> = {};

    request: Promise<string | null> | null = null;

    isFetching: boolean = false;

    private async fetchToken(assetId: number, vendor: string) {
        this.isFetching = true;

        const url = `${config.api.tokenV2Url}${vendor}/${assetId}`;
        const response = await fetch(url, { credentials: 'include' });

        this.isFetching = false;

        if (!response.ok) {
            return Promise.reject(TOKEN_FETCH_ERROR);
        }

        this.data = await response.json();
        return this.getValue();
    }

    public async fetch(stream: StreamModel) {
        if (!this.isValid()) {
            if (this.request && this.isFetching) {
                return this.request;
            }

            if (stream.isSecure()) {
                this.request = this.fetchToken(
                    stream.getId(),
                    stream.getVendor(),
                );
                return this.request;
            }
            this.request = Promise.resolve(null);
        }

        return this.request;
    }

    private getValue() {
        const { signedHlsUrl } = this.data;
        if (!signedHlsUrl) {
            return null;
        }
        return new URL(signedHlsUrl).searchParams.get('hdnea');
    }

    public async getSignedUrl(stream: StreamModel) {
        try {
            await this.fetch(stream);
        } catch (error) {
            if (Number.isInteger(error)) {
                return null;
            }
            throw error;
        }
        if (!this.isValid()) {
            return null;
        }
        return this.data.signedHlsUrl || null;
    }

    public isValid() {
        // on load expiry is 0 therefore it will always return false
        return Date.now() < this.data.expiry * 1000;
    }

    public async getDrmToken(stream: StreamModel) {
        try {
            await this.fetch(stream);
        } catch (error) {
            if (Number.isInteger(error)) {
                return null;
            }
            throw error;
        }
        if (!this.isValid()) {
            return null;
        }
        return this.data.drmToken?.value || null;
    }

    public isDrmTokenValid() {
        return Date.now() < (this.data.drmToken?.expiry || 0) * 1000;
    }
}

export default TokenV2;

export type TokenPayload = {
    expiry: number;
    hasAccess: boolean;
    loggedIn: boolean;
    signedHlsUrl: string;
    value: string;
    drmToken?: {
        value: string;
        expiry: number;
    };
};
